import {
  Body,
  Box,
  Divider,
  Heading,
  HStack,
  SimpleGrid,
  Link,
  VStack,
} from '@hover/blueprint';
import { Link as GatsbyLink } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import React, { FC, Fragment } from 'react';
import { AICPA_LINK } from '../../../constants';
import { ILinksGroup } from '../../../interfaces/ILinksGroup';
import { chunk } from '../../../utils';
import { FooterContact } from './FooterContact';
import { FooterProps } from './types';
import BottomLinkDesktop from './BottomLinkDesktop';

export interface FooterDesktopProps extends FooterProps {}

const FooterDesktop: FC<FooterDesktopProps> = ({
  contact,
  copyrightText,
  footerBottomItems,
  links,
}) => {
  const chunkedLinks = chunk(links, 2);

  return (
    <>
      <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} pb={1000} px={200}>
        <Box
          display="flex"
          alignItems="flex-start"
          height="100%"
          flexDir="column"
        >
          <>
            <FooterContact {...contact} />
            <Box marginTop="auto">
              <a href={AICPA_LINK} aria-label="soc logo">
                <StaticImage
                  placeholder="blurred"
                  src="../../../assets/images/soc_logo.png"
                  alt="SOC logo service organization"
                  width={80}
                />
              </a>
            </Box>
          </>
        </Box>
        {chunkedLinks.map((section: ILinksGroup) => {
          const key = section.reduce((acc, item) => acc.concat(item.label), '');
          return (
            <VStack spacing={400} alignItems="flex-start" pl={1000} key={key}>
              {section.map((group, index) => (
                <Fragment key={group.label}>
                  <Heading
                    as="p"
                    color="neutral.0"
                    size={200}
                    pt={[1, 2].includes(index) ? '72px' : 'none'}
                    marginBottom={0}
                    textTransform="uppercase"
                  >
                    {group.label}
                  </Heading>
                  {group.links?.map(link =>
                    link.isExternal ? (
                      <Link
                        href={link.to}
                        isExternal
                        key={link.label}
                        isMinimal
                        size={500}
                        fontWeight="body"
                        color="brandNavy.200"
                        _hover={{ color: 'primary.500' }}
                        _active={{ color: 'primary.300' }}
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <Link
                        as={GatsbyLink}
                        to={link.to}
                        key={link.label}
                        isMinimal
                        size={500}
                        fontWeight="body"
                        color="brandNavy.200"
                        _hover={{ color: 'primary.500' }}
                        _active={{ color: 'primary.300' }}
                      >
                        {link.label}
                      </Link>
                    ),
                  )}
                </Fragment>
              ))}
            </VStack>
          );
        })}
      </SimpleGrid>
      <Divider
        borderColor="brandNavy.300"
        orientation="horizontal"
        borderWidth="1px"
        borderTopWidth={0}
        opacity={1}
      />
      <Box justifyContent="space-between" py={700}>
        <Body
          color="brandNavy.200"
          size={300}
          isSemiBold
          textTransform="uppercase"
          as={GatsbyLink}
          to="https://hover.to/"
        >
          {copyrightText}
        </Body>
        <HStack spacing={700}>
          {footerBottomItems.map(({ label, to }) =>
            to.length ? (
              <BottomLinkDesktop
                key={label}
                isExternalLink={to.includes('https://')}
                linkTo={to}
                label={label}
              />
            ) : (
              <Body
                key={label}
                cursor="pointer"
                className="ot-sdk-show-settings"
                color="brandNavy.200"
                _hover={{ color: 'primary.500' }}
                _active={{ color: 'primary.300' }}
                size={300}
                fontWeight="bodyBold"
                textTransform="uppercase"
              >
                {label}
              </Body>
            ),
          )}
        </HStack>
      </Box>
    </>
  );
};

export default FooterDesktop;
